import { ConfigProvider, DatePicker, Form, Input, message, Modal, Select, Button } from "antd";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import container from "@/lib/di/container";
import React from "react";
import { dayjsPipe } from "@/core/pipes/dayjs.pipe";
import { WaterSupplySuspensionModel } from "@/core/models/water-supply-suspension.model";
import { WaterSupplySuspensionRepo } from "@/core/repos/tables/water-supply-suspension.repo";
import { SUSPENSION_STATUSES, suspensionStatusPipe } from "@/core/pipes/suspension-status.pipe";
import { MODAL_THEME } from "@/core/themes/modal.theme";
import { usePermissionStore } from "@/core/stores/permission.store";
import { SaveButton } from "@/app/home/components/table/SaveButton";
import { UploadFormHandle, UploadForm } from "@/app/home/components/upload/UploadForm";
import { SyncOutlined } from '@ant-design/icons';
import { useDrafts } from '@/core/stores/drafts.store';
import { ResetDraftButton } from '@/app/home/components/drafts/ResetDraftButton';
import { DEF_DATE_FORMAT, DEF_DATETIME_FORMAT } from "@/app/home/utils/global.utils";

const waterSupplySuspensionRepo = container.get(WaterSupplySuspensionRepo);

interface DetailModalProps {
    isModalOpen: boolean,
    setIsModalOpen: Dispatch<SetStateAction<boolean>>,
    data: WaterSupplySuspensionModel,
}

const INPUT_WIDTH = 450;

export const DetailModal: React.FC<DetailModalProps> = ({isModalOpen, setIsModalOpen, data}) => {
    const permissionStore = usePermissionStore();
    const [form] = Form.useForm<WaterSupplySuspensionModel>();
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [messageApi, contextHolder] = message.useMessage();
    const uploadFormRef = useRef<UploadFormHandle>(null);
    // Draft logic for WaterSupplySuspensionModel
    const draft = useDrafts((state) => state.getDraft('WaterSupplySuspensionModel'));
    const _saveDraft = useDrafts((state) => state.saveDraft);
    const saveDraft = () => _saveDraft('WaterSupplySuspensionModel', form.getFieldsValue());
    const _clearDraft = useDrafts((state) => state.clearDraft);
    const clearDraft = () => {
        _clearDraft('WaterSupplySuspensionModel');
        form.setFieldsValue(data);
    };
    // Attach hooks to handle data changes
    const handleDataChange = async () => {
        form.setFieldsValue(data);
        if (data.id && data.id.length > 0) {
            setIsEdit(true);
        } else {
            if (draft) form.setFieldsValue(draft);
            setIsEdit(false);
        }
    }
    useEffect(() => {
        handleDataChange();
    }, [data])

    // Initialize select options data
    const init = async () => {
        // await populateTollAreas();
    }
    // componentDidMount equivalent, run exclusively on client-side
    useEffect(() => {init()}, []);


    const handleSave = async () => {
        try {
            setLoading(true);
            const formData = await form.validateFields();
            if (isEdit) {
                formData.id = data.id;
                const result = await waterSupplySuspensionRepo.update({...data, ...formData});
                setIsModalOpen(false);
                if (result) {
                    await handleUpload(data.id);
                    messageApi.success({
                        content: "Đã cập nhật bản ghi"
                    })
                } else {
                    handleUploadReset();
                    messageApi.error({
                        content: "Cập nhật bản ghi thất bại"
                    })
                }
            } else {
                const result = await waterSupplySuspensionRepo.create({...data, ...formData});
                setIsModalOpen(false);
                clearDraft();
                if (result) {
                    await handleUpload(result.id);
                    messageApi.success({
                        content: "Thêm bản ghi mới thành công"
                    })
                } else {
                    handleUploadReset();
                    messageApi.error({
                        content: "Thêm bản ghi mới thất bại"
                    })
                }
            }
        } catch (error) {
            console.log(error);
        }
        setLoading(false);
    };
    const handleCancel = () => {
      if (!isEdit) saveDraft();
      setIsModalOpen(false);
    };
    const handleUpload = async (referenceId: string) => {
        if (uploadFormRef.current) await uploadFormRef.current.saveUploadedFiles(referenceId);
    }
    const handleUploadReset = () => {
        if (uploadFormRef.current) uploadFormRef.current.reset();
    }

    return (
        <ConfigProvider theme={MODAL_THEME}>
            {contextHolder}
            <Modal open={isModalOpen} onCancel={handleCancel} width={isEdit?980:510}
                centered
                styles={{
                    mask: {
                        backdropFilter: 'blur(2px)',
                        backgroundColor: 'var(--primary-mask)'
                }
                }}
                footer={[
                    <div key={1} className="w-full flex flex-row justify-end px-1.5">
                        <SaveButton isEdit={isEdit} loading={loading} onClick={handleSave}/>
                    </div>,
                ]}>
                <div className="flex flex-row w-full items-center justify-start select-none">
                    <p className="ml-2 text-2xl font-bold text-primary">THÔNG TIN CHI TIẾT</p>
                    {!isEdit && <ResetDraftButton onClick={clearDraft} />}
                </div>
                <div className="flex flex-row justify-center mt-4">
                    <Form<WaterSupplySuspensionModel>
                        name="basic"
                        className="flex flex-col items-end"
                        form={form}
                        layout="vertical"
                        disabled={isEdit&&!permissionStore.page.edit}
                    >
                        <div className="flex">
                        <div className="flex flex-col">
                            <Form.Item<WaterSupplySuspensionModel>
                                label="Tên"
                                name="name"
                                rules={[{ required: true, message: 'Trường này là bắt buộc!' }]}
                            >
                                <Input style={{width: INPUT_WIDTH}}></Input>
                            </Form.Item>

                            <Form.Item<WaterSupplySuspensionModel>
                                label="Trạng thái"
                                name="status"
                                rules={[{ required: true, message: 'Trường này là bắt buộc!' }]}
                            >
                                <Select showSearch placeholder="Chọn đối tượng" style={{width: INPUT_WIDTH}}>
                                    {SUSPENSION_STATUSES.map(type => <Select.Option key={type} value={type}>{suspensionStatusPipe(type)}</Select.Option>)}
                                </Select>
                            </Form.Item>
                            <Form.Item<WaterSupplySuspensionModel>
                                label="Lý do ngưng cấp nước"
                                name="reason"
                            >
                                <Input.TextArea style={{width: INPUT_WIDTH, height: 120, resize: "none", marginRight: 0}}></Input.TextArea>
                            </Form.Item>
                        </div>
                        {isEdit?<div className="flex flex-col w-1/2 pl-4">
                            <Form.Item<WaterSupplySuspensionModel>
                                label="Người tạo"
                                name="createdBy"
                            >
                                <Input style={{width: INPUT_WIDTH}} disabled></Input>
                            </Form.Item>
                            <Form.Item<WaterSupplySuspensionModel>
                                label="Ngày tạo"
                                name="createdAt"
                                getValueProps={(i) => ({ value: dayjsPipe(i) })}
                            >
                                <DatePicker format={DEF_DATETIME_FORMAT} showTime disabled style={{width: INPUT_WIDTH}}></DatePicker>
                            </Form.Item>
                            <Form.Item<WaterSupplySuspensionModel>
                                label="Người cập nhật"
                                name="updatedBy"
                            >
                                <Input style={{width: INPUT_WIDTH}} disabled></Input>
                            </Form.Item>
                            <Form.Item<WaterSupplySuspensionModel>
                                label="Ngày cập nhật"
                                name="updatedAt"
                                getValueProps={(i) => ({ value: dayjsPipe(i) })}
                            >
                                <DatePicker format={DEF_DATETIME_FORMAT} showTime disabled style={{width: INPUT_WIDTH}}></DatePicker>
                            </Form.Item>
                        </div>:null}
                        </div>
                    </Form>
                </div>
                <div className="mb-4 mx-2 border-t border-dashed border-gray-300"></div>
                <div className="px-1.5">
                    <UploadForm 
                        ref={uploadFormRef as any}
                        objectType={"WaterSupplySuspension"}
                        referenceId={data.id.length > 0?data.id:undefined}
                        text="Văn bản">
                    </UploadForm>
                </div>
                <div className="mb-5 mt-4 mx-2 border-t border-dashed border-gray-300"></div>
            </Modal>
        </ConfigProvider>
    )
}